import { InputProps } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { MdSearch } from 'react-icons/md';
import { useDebounce } from '../../hooks/useDebounce';
import { useNonInitialEffect } from '../../hooks/useNonInitialEffect';
import { pluralize } from '../../utils';
import MCircularProgress from './MCircularProgress';
import MCloseButton from './MCloseButton';
import MText from './MText';
import { MIcon, MInputGroup } from './chakra';
import MInput from './MInput';

const START_ELEMENT_PROPS = { w: '8', px: '0' } as const;

interface MSearchInputProps extends Omit<InputProps, 'onChange'> {
  value: string;
  count?: number;
  omitCount?: boolean;
  loading?: boolean;
  onChange: (val: string) => void;
}

export const MSearchInput = React.forwardRef<any, MSearchInputProps>(
  (
    {
      children,
      value,
      omitCount,
      count,
      loading,
      onChange,
      ...rest
    }: MSearchInputProps,
    ref,
  ) => {
    const [internalValue, setInternalValue] = useState(value);
    const debouncedFilters = useDebounce(internalValue);

    // don't notify parent on first render
    useNonInitialEffect(() => {
      onChange(debouncedFilters);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedFilters]);

    useEffect(() => {
      setInternalValue(value);
    }, [value]);

    const handleOnKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>) => {
      if (ev.key === 'Enter') {
        onChange(internalValue);
      }
    };
    const handleOnChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
      setInternalValue(ev.target.value);
    };

    const onSetEmpty = () => {
      onChange('');
    };

    // Width of right element to ensure input text does not overlap
    let rightW = 0;

    if (!omitCount && internalValue && !loading) {
      rightW += 22;
    } else if (internalValue) {
      rightW += 8;
    }

    if (loading) {
      rightW += 6;
    }

    rightW = rightW * 4;

    // `InputGroup` (not a bare relative `Box`) pads the input start past the
    // icon, as v2's `InputGroup` did — without it the placeholder runs under
    // the icon. It also pins the end element to the input's own box: v3's
    // `InputElement` has no `top`, so outside a group it drops below the input.
    return (
      <MInputGroup
        startElement={<MIcon w="4" h="4" as={MdSearch} color="tPurple.base" />}
        startElementProps={START_ELEMENT_PROPS}
        endElement={
          rightW > 0 ? (
            <>
              {!omitCount && !!internalValue && !loading && (
                <MText fontSize="xs" whiteSpace="nowrap">
                  {count
                    ? `${count} ${pluralize('Result', count)}`
                    : 'No Results'}
                </MText>
              )}

              {loading && (
                <MCircularProgress mx={1} isIndeterminate size="2xs" />
              )}
              {!!internalValue && (
                <MCloseButton
                  aria-label="Clear search"
                  onClick={onSetEmpty}
                  size="sm"
                />
              )}
            </>
          ) : undefined
        }
        endElementProps={{ width: `${rightW}px` }}
      >
        <MInput
          ref={ref}
          value={internalValue}
          onKeyDown={handleOnKeyDown}
          onChange={handleOnChange}
          _placeholder={{ color: 'tPurple.base', fontWeight: '400' }}
          // Logical `pe`, not `pr`: it replaces the `pe` InputGroup injects
          // instead of competing with it.
          pe={`${rightW}px`}
          {...rest}
        />
      </MInputGroup>
    );
  },
);

export default MSearchInput;
